'use client';

import STORAGE_KEYS from '@/constant/storage';
import { useEffect, useState } from 'react';

/**
 * This script runs immediately on the client side to detect compatibility
 * before any other code that might cause crashes
 */
if (typeof window !== 'undefined') {
  // Immediately check for compatibility before anything else loads
  try {
    // We don't assign this to prevent any errors from leaking
    new RegExp('(?<=test)');
  } catch (e) {
    // If we detect incompatibility, set a flag in sessionStorage
    // This way we don't need to run the test again
    sessionStorage.setItem('browser-compatibility-issue', 'true');
    console.warn('Browser compatibility issue detected: RegExp lookbehind not supported');
  }
}

/**
 * Detect Safari version
 * @returns Safari version number or null (if not Safari)
 */
function detectSafariVersion(): number | null {
  if (typeof window === 'undefined') return null;
  
  const ua = window.navigator.userAgent;
  const isSafari = ua.indexOf('Safari') !== -1 && ua.indexOf('Chrome') === -1;
  
  if (!isSafari) return null;
  
  // Safari version detection
  // Example: 'Version/15.4 Safari/605.1.15'
  const versionMatch = ua.match(/Version\/(\d+)\.(\d+)/);
  if (versionMatch) {
    const majorVersion = parseInt(versionMatch[1], 10);
    const minorVersion = parseInt(versionMatch[2], 10);
    
    // Return format: 15.4 => 15.4
    return majorVersion + (minorVersion / 10);
  }
  
  return null;
}

/**
 * This component should be rendered as early as possible in the app lifecycle
 * to warn users about compatibility issues before they occur
 */
export default function BrowserCompatibilityAlert() {
  const [isVisible, setIsVisible] = useState(false);
  const [browserInfo, setBrowserInfo] = useState<string>('');
  
  // Run detection as soon as component mounts
  useEffect(() => {
    // Check if the user has dismissed the warning
    const warningDismissed = localStorage.getItem(STORAGE_KEYS.BROWSER_COMPATIBILITY_WARNING_DISMISSED);
    if (warningDismissed === 'true') return;
    
    // Check if we already detected an issue during initial script execution
    const hasCompatibilityIssue = sessionStorage.getItem('browser-compatibility-issue') === 'true';
    
    if (hasCompatibilityIssue) {
      // Detect browser info for better messaging
      const safariVersion = detectSafariVersion();
      
      if (safariVersion !== null) {
        setBrowserInfo(`Safari ${safariVersion}`);
      } else {
        setBrowserInfo('Your browser');
      }
      
      setIsVisible(true);
    }
  }, []);
  
  const dismissWarning = () => {
    setIsVisible(false);
    // Remember user preference
    localStorage.setItem(STORAGE_KEYS.BROWSER_COMPATIBILITY_WARNING_DISMISSED, 'true');
  };
  
  if (!isVisible) return null;
  
  return (
    <div className="fixed top-0 left-0 w-full bg-amber-100 border-b border-amber-200 px-4 py-3 z-50 text-center shadow-md">
      <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-amber-800 relative pr-8">
        {/* Close button */}
        <button 
          onClick={dismissWarning}
          className="absolute right-0 top-0 sm:top-1/2 sm:-translate-y-1/2 text-amber-600 hover:text-amber-800 transition-colors"
          aria-label="Close warning"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
        
        <div className="flex items-center justify-center sm:justify-start gap-2 min-w-fit">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="font-medium text-sm">Browser Compatibility Alert</span>
        </div>
        
        <div className="flex-1 text-sm text-left">
          <p className="mb-1">{browserInfo} may not fully support all features needed for this application. Some functionality might not work correctly.</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 items-center">
            <p className="text-xs">
              <span>We recommend: </span>
              <span className="font-medium ml-1">Safari 16.4+</span>,
              <span className="font-medium"> Chrome</span> or
              <span className="font-medium"> Firefox</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 